import { EnumHelper } from '@/helpers/enumHelper';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Company, MarketPlaceService, PropertyCompanyService, ServiceType, User, UserSummary } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelectComponent } from '../ui-forms/select/select.component';

import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { InputComponent } from '../ui-forms/input/input.component';

@Component({
    selector: 'app-property-services-association',
    templateUrl: './property-services-association.component.html',
    styleUrls: ['./property-services-association.component.scss'],
    standalone: true,
    imports: [NgIf, FormsModule, ReactiveFormsModule, MatButtonModule, SelectComponent, NgFor, MatProgressSpinnerModule, TranslateModule, TooltipDirective, InputComponent]
})
export class PropertyServicesAssociationComponent implements OnInit, OnDestroy {
  @Input() propertyUid = '';

  getPropertyCompanyServicesSub = new Subscription();
  addPropertyCompanyServicesSub = new Subscription();
  isLoading = false;
  users: UserSummary[] = [];
  autoCompletedOptions: User[];
  serviceList: ServiceType[] = EnumHelper.getApiEnumList(ServiceType);
  isAddingService = false;
  autocompletionExcludeIds = [];

  get form_services() {
    return this.form.get('services') as FormArray;
  }
  get form_filterServices() {
    return this.form.get('filterServices') as FormControl<ServiceType>;
  }

  form = this.fb.group({
    services: this.fb.array([]),
    filterServices: [null]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private cdref: ChangeDetectorRef,
    private marketPlaceService: MarketPlaceService
  ) {}

  ngOnInit(): void {
    this.form_services.valueChanges.subscribe((val: PropertyCompanyService[]) => {
      this.cdref.detectChanges();
      this.autocompletionExcludeIds =
        [...this.form_services?.controls?.map((a: FormControl<PropertyCompanyService>) => a?.value?.uid)] || [];
    });
    this.getPropertyCompanyServices();
  }

  ngOnDestroy(): void {
    this.getPropertyCompanyServicesSub.unsubscribe();
    this.addPropertyCompanyServicesSub.unsubscribe();
  }

  addServiceField(service?: PropertyCompanyService) {
    if (!service) {
      service = {};
    }

    this.form_services.insert(0, this.fb.control(service));

    this.setIsAddingService();
  }

  onDeleteService(company: PropertyCompanyService) {
    this.removePropertyCompanyService(company?.uid);
  }

  onAddService(company: Company) {
    this.addPropertyCompanyService(company.uid);
  }

  removeServiceInForm(companyId: string) {
    const found = this.form_services.controls.findIndex(
      (u: FormControl<PropertyCompanyService>) => u.value?.uid === companyId
    );
    if (found >= 0) {
      this.form_services.removeAt(found);
    }
  }

  setIsAddingService() {
    const found = this.form_services.value.find((s: PropertyCompanyService) => !s?.uid);
    if (found) {
      this.isAddingService = true;
    } else {
      this.isAddingService = false;
    }
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getPropertyCompanyServices() {
    this.isLoading = true;
    this.getPropertyCompanyServicesSub = this.marketPlaceService
      .getPropertyCompanyServices(this.propertyUid)
      .subscribe({
        next: (services: PropertyCompanyService[]) => {
          this.isLoading = false;

          services.forEach((p) => {
            this.addServiceField(p);
          });
        }
      });
  }

  addPropertyCompanyService(companyId: string) {
    this.isLoading = true;
    this.addPropertyCompanyServicesSub = this.marketPlaceService
      .addPropertyCompanyService(this.propertyUid, companyId)
      .subscribe({
        next: (res: void) => {
          this.isLoading = false;
          this.form_services.clear();
          this.form_filterServices.setValue(null);
          this.setIsAddingService();
          this.getPropertyCompanyServices();
        }
      });
  }

  removePropertyCompanyService(companyId: string) {
    this.isLoading = true;
    this.addPropertyCompanyServicesSub = this.marketPlaceService
      .removePropertyCompanyService(this.propertyUid, companyId)
      .subscribe({
        next: (res: void) => {
          this.isLoading = false;
          this.removeServiceInForm(companyId);
        }
      });
  }
}
